import difference from 'lodash/difference';
import flatMap from 'lodash/flatMap';
import flat from 'lodash/flatten';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import type { Moment } from 'moment';

import { WELLNESS_APPOINTMENT_STEP } from '../constants/appointment';
import { SURCHARGES_CONFIG_BY_COUNTRY } from '../constants/country';
import { isAppointmentDailySurcharged } from '../prices/common-prices';
import type {
  WellnessHourSlot as HourSlot,
  MarkedSlots,
  Pro,
  Unavailability,
  Universe,
  Weekday,
  WellnessHourSlot,
} from '../types/commons';
import {
  type AppointmentInterval,
  getAppointmentIntervalsFromHourSlots,
  getSurchargedSlots,
  getSurchargeText,
  getWellnessSlotsBetween,
  roundDownToStep,
  roundUpToStep,
} from './appointment';
import { getCurrentCountry } from './country';
import moment from './moment';
import { isAbsent } from './previous-pros';
import { formatStandardTime } from './time';
import { isCleaning } from './universes';

export const initializeSlotsDictionary = (openSlots: WellnessHourSlot[]): MarkedSlots =>
  openSlots.reduce(
    (acc, time) => ({
      ...acc,
      [time]: {
        isPrimary: false,
        isSecondary: false,
        isHighlighted: false,
        pros: [],
        nearPros: [],
        surcharge: null,
      },
    }),
    {},
  );

export const getUnavailabilitiesForDate = (date: Moment, unavailabilities: Unavailability[]): Unavailability[] =>
  unavailabilities.filter((unavailability) => {
    const begin = moment(unavailability.begin);
    const end = moment(unavailability.end);
    const startOfDay = date.clone().startOf('day');
    const endOfDay = date.clone().endOf('day');
    /*
     * We use `end.isSameOrBefore(startOfDate)` for comparison
     * so that we ignore events ending at the selected date at midnight.
     */
    return !(end.isSameOrBefore(startOfDay) || begin.isSameOrAfter(endOfDay));
  });

export const getSlotsWhereProUnavailable = (
  date: Moment,
  proUnavailabilitiesForDate: Unavailability[],
  openSlots: WellnessHourSlot[],
): WellnessHourSlot[] => {
  const allSlotsWhereProUnavailable = uniq(
    flat(
      proUnavailabilitiesForDate.map((unavailability: Unavailability) => {
        const begin = moment(unavailability.begin);
        const end = moment(unavailability.end);
        const doesUnavailabilityBeginOnDate = begin.isSame(date, 'day');
        const doesUnavailabilityEndOnDate = end.isSame(date, 'day');

        // Either the unavailability begins on date, or we take 00:00 of the date by default
        const min = doesUnavailabilityBeginOnDate ? begin : date.clone().startOf('day');
        // Either the unavailability ends on date, or we take 24:00 of the date by default
        const max = doesUnavailabilityEndOnDate ? end : date.clone().endOf('day');
        const step = WELLNESS_APPOINTMENT_STEP;

        return getWellnessSlotsBetween({
          // We round down the begin time as we want to have the following:
          // If an unavailability begins at the time 17:03 and ends at 17:15 for example, the slot
          // 17:00 should be unavailable and the slot 17:30 should be available
          min: min.clone().set({ minute: roundDownToStep(min.minute(), step) }),
          // We round up the end time as we want to have the following:
          // If an unavailability ends at the time 17:15 for example, the slot
          // 17:00 should be unavailable and the slot 17:30 should be available.
          max: max.clone().set({ minute: roundUpToStep(max.minute(), step) }),
        });
      }),
    ),
  );

  // Remove closed time slots from unavailable slots
  return intersection(openSlots, allSlotsWhereProUnavailable);
};

/**
 * Filters openSlots by deleting slots where pro does not work or is absent.
 */
export const getProOpenHoursSlots = (openSlots: WellnessHourSlot[], date: Moment, pro: Pro): WellnessHourSlot[] => {
  const openingHoursForDate = pro.opening_hours.filter(({ weekday }) => weekday === date.day());

  // There are no slots if pro has no opening hours for date or if date is at time where pro
  // has declared an absence
  if (openingHoursForDate.length === 0 || isAbsent(pro, date)) {
    return [];
  }

  // If we have opening hours like
  // [
  //   { weekday: 0, start: { hour: 8, minute: 0 }, end: { hour: 10, minute: 0 }  },
  //   { weekday: 0, start: { hour: 19, minute: 0 }, end: { hour: 20, minute: 0 }  },
  // ]
  // this will give the following result here:
  // ['08:00', '08:30', '09:00', '09:30', '19:00', '19:30']
  const allOpenHoursSlots = flatMap(openingHoursForDate, ({ start, end }) =>
    getWellnessSlotsBetween({
      min: moment().set(start),
      max: moment().set(end),
    }),
  );

  // intersection with openSlots to remove closed time slots from allOpenHoursSlots
  return intersection(openSlots, allOpenHoursSlots);
};

export const getNearbySlotsForDate = (date: Moment, unavailability: Unavailability): WellnessHourSlot[] => {
  const { near } = unavailability;
  if (!near) return [];

  const begin = moment(near.begin);
  const end = moment(near.end);
  const beginsToday = begin.isSame(date, 'day');
  const endsToday = end.isSame(date, 'day');

  const beginSlots = beginsToday
    ? getWellnessSlotsBetween({
        min: begin,
        max: moment(unavailability.begin),
      })
    : [];

  const endSlots = endsToday
    ? getWellnessSlotsBetween({
        min: moment(unavailability.end),
        max: end,
      })
    : [];

  return [...beginSlots, ...endSlots];
};

const getProNearbySlots = (
  date: Moment,
  areAllWantedProChoicesExclusive: boolean,
  doesProLiveNearUser: boolean,
  proUnavailabilitiesForDate: Unavailability[],
  proAvailableSlots: WellnessHourSlot[],
): WellnessHourSlot[] => {
  if (!areAllWantedProChoicesExclusive) return [];
  // If pro lives nearby user, all available slots are nearby
  if (doesProLiveNearUser) return proAvailableSlots;

  const allNearbyslots = uniq(
    proUnavailabilitiesForDate.reduce(
      (nearbySlots: WellnessHourSlot[], unavailability: Unavailability) =>
        nearbySlots.concat(getNearbySlotsForDate(date, unavailability)),
      [],
    ),
  );

  return intersection(proAvailableSlots, allNearbyslots);
};

const setProAvailableSlots = (markedSlots: MarkedSlots, proAvailableSlots: WellnessHourSlot[], pro: Pro): MarkedSlots =>
  proAvailableSlots.reduce(
    (markedSlotsAcc: MarkedSlots, slot: WellnessHourSlot) => ({
      ...markedSlotsAcc,
      [slot]: {
        ...markedSlotsAcc[slot],
        pros: [...markedSlotsAcc[slot]!.pros, pro],
      },
    }),
    markedSlots,
  );

const setProNearbySlots = (markedSlots: MarkedSlots, nearbySlots: WellnessHourSlot[], pro: Pro): MarkedSlots =>
  nearbySlots.reduce(
    (markedSlotsAcc, slot) => ({
      ...markedSlotsAcc,
      [slot]: {
        ...markedSlotsAcc[slot],
        nearPros: [...markedSlotsAcc[slot]!.nearPros, pro],
      },
    }),
    markedSlots,
  );

export const getProAvailableSlotsMinusSessionDuration = (
  slotsWhereProAvailable: WellnessHourSlot[],
  basketDuration: number,
) => {
  // For slots like ['07:00', '07:30', '10:00'], the intervals would be
  // [{ start: '07:00', end: '08:00' }, { start: '10:00', end: '10:30' }]
  const allAppointmentIntervals = getAppointmentIntervalsFromHourSlots(slotsWhereProAvailable);

  // We round to the next higher step so that slotsMinusDuration does not contain "invalid slots"
  // when the basket duration is uneven.
  // For example: The basket duration could be 50min and for an interval
  // like { start: '07:00', end: '10:00' }, we could then obtain a slot like '09:10' during the
  // flatMap later. By rounding the basket duration to the next higher step (i.e. 60min), we
  // always obtain valid slots during the flatMap (i.e. `'09:00'` in our case).
  const basketDurationRoundedToNextStep = roundUpToStep(
    // If the basket duration is 0, we need to use the step by default as otherwise
    // we would add an extra slot!
    // For example: The available slots are ['11:00', '11:30'], then the interval would be
    // [{ start: '11:00', end: '12:00' }] and therefore we would include '12:00' as available
    // slot although it should not have been available. That's why we subtract the step.
    basketDuration || WELLNESS_APPOINTMENT_STEP,
    WELLNESS_APPOINTMENT_STEP,
  );

  const slotsMinusDuration = flatMap(allAppointmentIntervals, ({ start, end }: AppointmentInterval) =>
    getWellnessSlotsBetween({
      min: start,
      max: end.clone().subtract(basketDurationRoundedToNextStep, 'minutes'),
      // Here we need to include the max because if the last available hour is 22:00 and
      // the session lasts 1h, the pro should be available at 21:00 but if we do not include
      // the max, the pro will be available at 20:30 max.
      // Same if the session lasts 45min, the pro should be available at 21:15 max (so 21:00 with
      // the available slots), but he will be available at 20:30 max
      // because we do not include the max.
      options: { includeMax: true },
    }),
  );

  // No need to do an intersection with open slots here as slotsWhereProAvailable has
  // already been calculated based on the open slots and also the roundUpToStep of the
  // basket duration makes sure to not include invalid slots
  return slotsMinusDuration;
};

type GetProAvailableSlotsParams = {
  openSlots: WellnessHourSlot[];
  date: Moment;
  pro: Pro;
  proUnavailabilitiesForDate: Unavailability[];
  basketDuration: number;
};

const getProAvailableSlots = ({
  openSlots,
  date,
  pro,
  proUnavailabilitiesForDate,
  basketDuration,
}: GetProAvailableSlotsParams): WellnessHourSlot[] => {
  // The slots where the pro has his/her opening hours for the selected date, subtracted
  // by the time slots that are closed.
  // This slot array will be empty, if the pro has declared an absence for this date
  // or if the pro is not working on this weekday (i.e. no entry for weekday in opening hours).
  const proOpenHoursSlots = getProOpenHoursSlots(openSlots, date, pro);
  if (proOpenHoursSlots.length === 0) return [];

  // All slots where the pro is unavailable for selected date
  const slotsWhereProUnavailable = getSlotsWhereProUnavailable(date, proUnavailabilitiesForDate, openSlots);

  // Remove slots where pro is unavailable
  const slotsWhereProAvailable = difference(proOpenHoursSlots, slotsWhereProUnavailable);

  // Take into account the session duration for each slot
  const proAvailableSlotsMinusBasketDuration = getProAvailableSlotsMinusSessionDuration(
    slotsWhereProAvailable,
    basketDuration,
  );

  // To be sure we do not include slots that are closed
  return intersection(openSlots, proAvailableSlotsMinusBasketDuration);
};

type IsProAvailableParams = {
  openSlots: WellnessHourSlot[];
  date: Moment;
  pro: Pro;
  basketDuration: number;
};

export const isProAvailable = ({ openSlots, date, pro, basketDuration }: IsProAvailableParams) => {
  if (moment(pro.next_available_day).isAfter(date)) return false;

  const proUnavailabilitiesForDate = getUnavailabilitiesForDate(date, pro.unavailabilities);
  return (
    getProAvailableSlots({
      openSlots,
      date,
      pro,
      proUnavailabilitiesForDate,
      basketDuration,
    }).length > 0
  );
};

type SetProsAndNearProsParams = {
  markedSlots: MarkedSlots;
  date: Moment;
  wantedPros: Pro[];
  areAllWantedProChoicesExclusive: boolean;
  basketDuration: number;
};

// TODO: add tests for this utils
const setProsAndNearPros = ({
  markedSlots,
  date,
  wantedPros,
  areAllWantedProChoicesExclusive,
  basketDuration,
}: SetProsAndNearProsParams): MarkedSlots =>
  wantedPros.reduce((markedSlotsAcc, pro) => {
    // Pro cannot have availabilities if the back returns us a next available day for
    // the pro which is after the selected day
    if (moment(pro.next_available_day).isAfter(date, 'day')) {
      return markedSlotsAcc;
    }

    const proUnavailabilitiesForDate: Unavailability[] = getUnavailabilitiesForDate(date, pro.unavailabilities);
    const openSlots = Object.keys(markedSlotsAcc) as WellnessHourSlot[];

    // all slots where pro is available
    const proAvailableSlots = getProAvailableSlots({
      openSlots,
      date,
      pro,
      proUnavailabilitiesForDate,
      basketDuration,
    });
    if (!proAvailableSlots.length) return markedSlotsAcc;

    // all slots where pro is nearby
    const proNearbySlots = getProNearbySlots(
      date,
      areAllWantedProChoicesExclusive,
      pro.near_user,
      proUnavailabilitiesForDate,
      proAvailableSlots,
    );

    // set pro for available slots in marked slots
    const withAvailableSlots: MarkedSlots = setProAvailableSlots(markedSlotsAcc, proAvailableSlots, pro);
    // set pro for nearby slots in marked slots
    const withAvailableAndNearbySlots: MarkedSlots = setProNearbySlots(withAvailableSlots, proNearbySlots, pro);

    return {
      ...markedSlotsAcc,
      ...withAvailableAndNearbySlots,
    };
  }, markedSlots);

const setSurcharges = (
  markedSlots: MarkedSlots,
  selectedDate: Moment,
  holidays: string[], // for example ['2017-04-17', '2017-05-01', '2017-05-08']
  universe: Universe,
) => {
  const areEarlyAndLateHourSurchargesEnabled =
    SURCHARGES_CONFIG_BY_COUNTRY[getCurrentCountry()].hasEarlyAndLateHourSurcharges;
  // if selectedDate is a sunday and sunday surcharges are enabled or if selectedDate is a holiday
  // isAppointmentDailySurcharged also checks the surcharges config internally
  const isAllDaySurcharged = isAppointmentDailySurcharged(selectedDate, holidays);

  const openSlots = Object.keys(markedSlots) as WellnessHourSlot[];

  let surchargedSlots: WellnessHourSlot[] = [];
  if (isAllDaySurcharged) {
    // All open slots are surcharged if whole day is surcharged
    surchargedSlots = openSlots;
  } else if (areEarlyAndLateHourSurchargesEnabled) {
    // Only early morning and late evening slots are surcharged
    surchargedSlots = intersection(openSlots, getSurchargedSlots());
  }

  const surchargeText = getSurchargeText(isCleaning(universe));

  return surchargedSlots.reduce(
    (markedSlotsAcc, slot) => ({
      ...markedSlotsAcc,
      [slot]: {
        ...markedSlotsAcc[slot],
        surcharge: surchargeText,
      },
    }),
    markedSlots,
  );
};

type InitializeMarkedSlotsParams = {
  openSlots: WellnessHourSlot[];
  selectedDate: Moment;
  wantedPros: Pro[];
  holidays: string[]; // for example ['2017-04-17', '2017-05-01', '2017-05-08']
  areAllWantedProChoicesExclusive: boolean;
  universe: Universe;
  basketDuration: number;
  disableSurcharges?: boolean;
};

/**
 * Creates a dictionary indexed by time (HH:mm).
 * Initialized with available pros, near pros and surcharges.
 */
export const initializeMarkedSlots = ({
  openSlots,
  selectedDate,
  wantedPros,
  holidays,
  areAllWantedProChoicesExclusive,
  universe,
  basketDuration,
  disableSurcharges = false,
}: InitializeMarkedSlotsParams) => {
  // The basic dictionary with all open slots
  const initialMarkedSlots: MarkedSlots = initializeSlotsDictionary(openSlots);
  // Set which pros are available and nearby for dictionary
  const withProsAndNearProsMarkedSlots: MarkedSlots = setProsAndNearPros({
    markedSlots: initialMarkedSlots,
    date: selectedDate,
    wantedPros,
    areAllWantedProChoicesExclusive,
    basketDuration,
  });
  // Set surcharges for each slot if any
  const withSurcharges = disableSurcharges
    ? withProsAndNearProsMarkedSlots
    : setSurcharges(withProsAndNearProsMarkedSlots, selectedDate, holidays, universe);

  // Fully initialised slot dictionary
  return withSurcharges;
};

const setPrimary = (markedSlots: MarkedSlots, primary: Moment | null): MarkedSlots => {
  if (!primary) return markedSlots;

  const primaryTime = formatStandardTime(primary) as WellnessHourSlot;
  return {
    ...markedSlots,
    [primaryTime]: {
      ...markedSlots[primaryTime],
      isPrimary: true,
    },
  };
};

const setSecondary = (markedSlots: MarkedSlots, secondary: Moment[]): MarkedSlots =>
  secondary.reduce((markedSlotsAcc, time) => {
    const secondaryTime = formatStandardTime(time) as WellnessHourSlot;
    return {
      ...markedSlotsAcc,
      [secondaryTime]: {
        ...markedSlotsAcc[secondaryTime],
        isSecondary: true,
      },
    };
  }, markedSlots);

export const setPrimaryAndSecondary = (
  markedSlots: MarkedSlots,
  primary: Moment | null,
  secondary: Moment[],
): MarkedSlots => {
  const withPrimary: MarkedSlots = setPrimary(markedSlots, primary);
  const withSecondary: MarkedSlots = setSecondary(withPrimary, secondary);

  return withSecondary;
};

type HighlightedSlotsParams = {
  markedSlots: MarkedSlots;
  selectedDate: Moment;
  highlightedTimeSlots: Moment[];
};

/**
 * Converts an array of Moment dates into time slots per weekday:
 * Record<Weekday, HourSlot[]>.
 * Slots are deduplicated and sorted for each day.
 */

const parseHighlightedTimeSlots = (isoDateStrings: Moment[]): Record<Weekday, HourSlot[]> => {
  const timeSlotsByWeekday = isoDateStrings.reduce<Record<Weekday, HourSlot[]>>(
    (acc, date) => {
      const day = date.day() as Weekday;
      const hour = formatStandardTime(date) as HourSlot;
      if (!acc[day].includes(hour)) acc[day].push(hour);
      return acc;
    },
    { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
  );
  Object.values(timeSlotsByWeekday).forEach((slots) => {
    slots.sort();
  });

  return timeSlotsByWeekday;
};

/**
 * Maps highlighted slot instants to unique sorted `HH:mm` strings.
 */
export const formatHighlightedTimeSlotsAsHourStrings = (highlightedTimeSlots: Moment[]): string[] =>
  uniq(highlightedTimeSlots.map((m) => formatStandardTime(m))).sort();

export const setHighlightedSlots = ({ markedSlots, selectedDate, highlightedTimeSlots }: HighlightedSlotsParams) => {
  const highlightedSlotsByDay = parseHighlightedTimeSlots(highlightedTimeSlots);
  const weekday = selectedDate.day() as Weekday;
  const highlightedSlots = highlightedSlotsByDay[weekday] ?? [];

  return highlightedSlots.reduce(
    (markedSlotsAcc, slot) => ({
      ...markedSlotsAcc,
      [slot]: {
        ...markedSlotsAcc[slot],
        isHighlighted: true,
      },
    }),
    markedSlots,
  );
};

type HasNearProTodayParams = {
  openSlots: WellnessHourSlot[];
  markedSlots: MarkedSlots;
};

export const hasNearProToday = ({ openSlots, markedSlots }: HasNearProTodayParams) =>
  openSlots.some((time) => !!markedSlots[time]?.nearPros.length);

type GetNearProsForSelectedDayParams = {
  openSlots: WellnessHourSlot[];
  markedSlots: MarkedSlots;
};

export const getNearProsForSelectedDay = ({ openSlots, markedSlots }: GetNearProsForSelectedDayParams) =>
  uniqBy(
    openSlots.reduce(
      (nearProsList: Pro[], time: WellnessHourSlot) => [...nearProsList, ...markedSlots[time]!.nearPros],
      [],
    ),
    'public_id',
  );

type HasAnySurchargeParams = {
  openSlots: WellnessHourSlot[];
  markedSlots: MarkedSlots;
};

export const hasAnySurcharge = ({ openSlots, markedSlots }: HasAnySurchargeParams) =>
  openSlots.some((time) => !!markedSlots[time]?.surcharge);
